<template>
    <nav class="pagination pagination--center">
        <button class="pages__prev" :disabled="!hasPrev" @click="prev">
            Prev
        </button>

        <div v-if="!isSimple" class="pages__holder">
            <div
                v-if="isTruncated"
                class="inline-block py-4 px-6 no-underline mr-2 text-base transition-all"
            >
                <m-icon size="13" stroke="2" name="more-horizontal"></m-icon>
            </div>
            <button
                v-for="page in pageCountDisplay"
                :key="page"
                class="pages__item"
                :class="[{ active: page === value }]"
                @click="changePage(page)"
            >
                {{ page }}
            </button>
            <div
                v-if="isTruncated"
                class="inline-block py-4 px-6 no-underline mr-2 text-base transition-all"
            >
                <m-icon size="13" stroke="2" name="more-horizontal"></m-icon>
            </div>
        </div>

        <button class="pages__next" :disabled="!hasNext" @click="next">
            Next
        </button>

        <small v-if="showPageCount" class="pages__count" @click="next">
            <span v-if="perPage == 1">{{ firstItem }} / {{ items }}</span>
            <div v-else>
                {{ firstItem }}-{{ Math.min(value * perPage, items) }} /
                {{ items }}
            </div>
        </small>
    </nav>
</template>

<script>
import MIcon from "../components/icons/icon.vue";

export default {
    name: "MPagination",
    components: {
        MIcon
    },
    props: {
        items: { type: Number, default: 0 },
        perPage: { type: [Number, String], default: 20 },
        value: { type: Number, default: 1 },
        isSimple: { type: Boolean, default: false },
        showPageCount: { type: Boolean, default: false },
        position: { type: String, default: "" },
        ariaNextLabel: { type: String, default: "" },
        ariaPreviousLabel: { type: String, default: "" },
        ariaPageLabel: { type: String, default: "" },
        ariaCurrentLabel: { type: String, default: "" },
        hasScroll: { type: Boolean, default: false }
    },
    computed: {
        isTruncated() {
            if (this.pageCount > 10) {
                return true;
            }
            return false;
        },
        pageCount() {
            return Math.ceil(this.items / this.perPage);
        },
        pageCountDisplay() {
            let pages = this.pageCount;
            if (pages <= 10) {
                return pages;
            } else {
                let array = [];
                let currentPage = this.value;

                if (currentPage - 2 > 0) {
                    array.push(currentPage - 2);
                }
                if (currentPage - 1 > 0) {
                    array.push(currentPage - 1);
                }

                array.push(currentPage);

                if (currentPage + 1 <= this.pageCount) {
                    array.push(currentPage + 1);
                }
                if (currentPage + 2 <= this.pageCount) {
                    array.push(currentPage + 2);
                }

                return array;
            }
        },
        firstItem() {
            const firstItem = this.value * this.perPage - this.perPage + 1;
            return firstItem >= 0 ? firstItem : 0;
        },
        hasPrev() {
            return this.value > 1;
        },
        hasNext() {
            return this.value < this.pageCount;
        }
    },
    methods: {
        prev(event) {
            this.changePage(this.value - 1, event);
        },
        next(event) {
            this.changePage(this.value + 1, event);
        },
        changePage(num, event) {
            if (this.hasScroll) {
                window.scrollTo({
                    top: 0,
                    behavior: "smooth"
                });
            }

            if (this.value === num || num < 1 || num > this.pageCount) return;
            this.$emit("input", num);
            // Set focus on element to keep tab order
            if (event && event.target) {
                this.$nextTick(() => event.target.focus());
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.pagination {
    @apply flex m-10 rounded justify-start;

    &--right {
        @apply justify-end;
    }

    &--center {
        @apply justify-center;
    }

    &--stretch {
        @apply justify-between;
    }

    .pages__holder {
        @apply hidden;

        @screen lg {
            @apply flex;
        }

        .pages__item {
            @apply inline-block py-4 px-6 no-underline mr-2 text-base transition-all;

            &:disabled {
                @apply cursor-not-allowed;
            }

            &:last-child {
                @apply mr-2;
            }
            &:hover {
                @apply text-white bg-blue-500;
            }
        }

        .pages__item.active {
            @apply text-white bg-blue-500 border-none;
        }
    }

    .pages__prev {
        @apply inline-block py-4 px-6 no-underline mr-2 bg-blue-100 font-bold;
    }
    .pages__next {
        @apply inline-block py-4 px-6 no-underline bg-blue-100 font-bold;
    }

    .pages__count {
        @apply flex flex-wrap content-center ml-4;
    }
}
</style>
